import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { validatePhoneNumber } from "../validations";
import { getTransactionUrl } from "../actions/orderActions";
import {
  clearProductCart,
  updateCartItemQuantity,
  setCartItemQuantity,
  removeCustomDesign,
  updateCustomDesignQuantity,
} from "../actions/cartActions";
import { ShoppingCart, PawPrint } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping, faTruckFast } from "@fortawesome/free-solid-svg-icons";

const ownerSchema = yup.object().shape({
  full_name: yup.string().required(),
  phone_number: yup
    .number()
    .required()
    .typeError("phone_number_type_error")
    .test({
      name: "phone_number",
      test: function (value) {
        return validatePhoneNumber(value, this);
      },
    }),
  second_phone_number: yup
    .string()
    .nullable()
    .notRequired()
    .test({
      name: "is-valid-phone",
      message: "Invalid phone number",
      test: function (value) {
        if (!value) return true;
        return validatePhoneNumber(value, this);
      },
    }),
  city: yup.string().required(),
  address: yup.string().required(),
});

const Checkout = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartState = useSelector((state) => state.cart);
  const userData = useSelector((state) => state.userData.userData);
  const isLoggedIn = !!userData;
  const [isGift, setIsGift] = useState(false);

  const PRODUCT_IMAGES = {
    "qr-collar": {
      orange: "/img/1-1.webp",
      cream: "/img/1-2.webp",
      black: "/img/1-3.webp",
      pink: "/img/1-4.webp",
    },
    "gps-tracker": {
      orange: "/img/circle-yellow-1.webp",
      cream: "/img/circle-blue-1.webp",
      black: "/img/circle-gray-1.webp",
      pink: "/img/circle-pink-1.webp",
    },
  };

  // 1. კალათის გასუფთავება 25 წუთში
  useEffect(() => {
    const checkExpiry = () => {
      const cartExpiry = localStorage.getItem("cart_expiry");
      const now = new Date().getTime();

      if (cartExpiry && now > parseInt(cartExpiry)) {
        dispatch(clearProductCart("qr-collar"));
        dispatch(clearProductCart("gps-tracker"));
        localStorage.removeItem("cart_expiry");
        localStorage.removeItem("fethub_cart");
      }
    };

    // ვამოწმებთ ჩატვირთვისას
    checkExpiry();

    // ვამოწმებთ ყოველ წუთს, რომ მომხმარებლის თვალწინ გაქრეს თუ დრო ამოიწურა
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // რეფრეშის შენარჩუნება (2 საათი)
  useEffect(() => {
    const TWO_HOURS = 2 * 60 * 60 * 1000;
    const expiry = Date.now() + TWO_HOURS;
    localStorage.setItem(
      "fethub_cart",
      JSON.stringify({
        data: cartState,
        expiry: expiry,
      }),
    );
  }, [cartState]);

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({ mode: "onSubmit", resolver: yupResolver(ownerSchema) });

  const calculateProductTotal = (cart) => {
    const standardTotal = Object.values(cart.colorQuantities).reduce(
      (sum, qty) => sum + qty,
      0,
    );
    const customTotal = cart.customDesigns
      .filter((d) => d.image)
      .reduce((sum, d) => sum + (d.quantity || 0), 0);
    return standardTotal + customTotal;
  };

  const qrCollarTotal = calculateProductTotal(cartState.qrCollar);
  const gpsTrackerTotal = calculateProductTotal(cartState.gpsTracker);
  const totalItems = qrCollarTotal + gpsTrackerTotal;

  const qrUnitPrice = 29;
  const gpsUnitPrice = 205;
  const shippingPrice = 0; // 6

  const qrSubtotal = qrUnitPrice * qrCollarTotal;
  const gpsSubtotal = gpsUnitPrice * gpsTrackerTotal;
  const subtotalAll = qrSubtotal + gpsSubtotal;
  const totalPrice = totalItems > 0 ? subtotalAll + shippingPrice : 0;

  const onPlaceOrder = (ownerData) => {
    const orderType =
      gpsTrackerTotal > 0 && qrCollarTotal === 0 ? "gps-collar" : "qr-collar";
    const cart =
      orderType === "gps-collar" ? cartState.gpsTracker : cartState.qrCollar;

    const formData = {
      ...ownerData,
      colorQuantities: cart.colorQuantities,
      customDesigns: cart.customDesigns.filter(
        (d) => d.image && d.quantity > 0,
      ),
      quantity: calculateProductTotal(cart),
      // Gift flag: collar won't be bound to the buyer; recipient claims
      // it by scanning the QR. Only meaningful when logged in — guests
      // never bind to an account either way.
      for_friend: isLoggedIn ? isGift : false,
    };

    // 2. კალათის გასუფთავება შეკვეთის გაფორმებისას
    dispatch(getTransactionUrl(formData, orderType));
    dispatch(clearProductCart("qr-collar"));
    dispatch(clearProductCart("gps-tracker"));
    localStorage.removeItem("cart_expiry");
    localStorage.removeItem("fethub_cart");
  };

  const renderCustomDesigns = (productType, customDesigns) => {
    // უსაფრთხოებისთვის ვამოწმებთ არის თუ არა მასივი
    const designsArray = Array.isArray(customDesigns) ? customDesigns : [];
    const validDesigns = designsArray.filter((d) => d.image && d.quantity > 0);

    if (validDesigns.length === 0) return null;

    const unitPrice =
      productType === "gps-tracker" ? gpsUnitPrice : qrUnitPrice;

    return validDesigns.map((design, index) => (
      <div
        key={`${productType}-custom-${design.id}`}
        className="cart-page-item custom-design-item"
      >
        <div className="cart-item-image">
          <img src="/img/1-5.webp" alt="Custom design" />
        </div>

        <div className="item-details">
          <span className="item-name">
            {t(productType === "gps-tracker" ? "gps tracker" : "qr tag")} -{" "}
            {t("custom design")} #{index + 1}
          </span>
          <div className="quantity-controls">
            <span>{`${design.quantity} ${t("piece")}`}</span>
          </div>
        </div>

        <span className="item-price">
          {unitPrice * design.quantity} {t("gel")}
        </span>
      </div>
    ));
  };

  const renderCartItem = (productType, color, quantity) => {
    if (quantity === 0) return null;

    const unitPrice =
      productType === "gps-tracker" ? gpsUnitPrice : qrUnitPrice;
    const itemImage = PRODUCT_IMAGES[productType]?.[color];

    return (
      <div key={`${productType}-${color}`} className="cart-page-item">
        <div className="cart-item-image">
          {itemImage ? (
            <img src={itemImage} alt={`${color} ${productType}`} />
          ) : (
            <div
              className="image-placeholder"
              style={{ backgroundColor: color }}
            ></div>
          )}
        </div>

        <div className="item-details">
          <span className="item-name">
            {t(productType === "gps-tracker" ? "gps tracker" : "qr tag")} -{" "}
            {t(color)}
          </span>

          <div className="quantity-controls">
            <span>{`${quantity} ${t("piece")}`}</span>
          </div>
        </div>

        <span className="item-price">
          {unitPrice * quantity} {t("gel")}
        </span>
      </div>
    );
  };

  if (totalItems === 0) {
    return (
      <div className="empty-cart-page">
        <div className="empty-cart-icon-and-quantity">
          {/* <ShoppingCart /> */}
          <FontAwesomeIcon icon={faCartShopping} />
          <span className="empty-cart-quantity">0</span>
        </div>

        <p>{t("cart_is_empty")}</p>
        <button onClick={() => navigate("/order/qrcollar")}>
          {t("buy now")}
        </button>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <form
        onSubmit={handleSubmit(onPlaceOrder)}
        className="checkout-container-layout"
      >
        <div className="product-section-parent-container">
          <h4>{t("cart")}</h4>

          <div className="product-section-inside-container">
            {qrCollarTotal > 0 && (
              <div className="product-section">
                {Object.entries(cartState.qrCollar.colorQuantities).map(
                  ([color, qty]) => renderCartItem("qr-collar", color, qty),
                )}
                {renderCustomDesigns(
                  "qr-collar",
                  cartState.qrCollar.customDesigns,
                )}
              </div>
            )}

            {gpsTrackerTotal > 0 && (
              <div className="product-section">
                {Object.entries(cartState.gpsTracker.colorQuantities).map(
                  ([color, qty]) => renderCartItem("gps-tracker", color, qty),
                )}
                {renderCustomDesigns(
                  "gps-tracker",
                  cartState.gpsTracker.customDesigns,
                )}
              </div>
            )}
          </div>

          <div className="cart-summary-box">
            {qrSubtotal > 0 && (
              <div className="section-subtotal">
                <span>{t("subtotal")}:</span>
                <span>
                  {qrSubtotal} {t("gel")}
                </span>
              </div>
            )}

            {gpsSubtotal > 0 && (
              <div className="section-subtotal">
                <span>
                  {t("gps tracker")} {t("subtotal")}:
                </span>
                <span>
                  {gpsSubtotal} {t("gel")}
                </span>
              </div>
            )}

            <div className="summary-line">
              <span>{t("shipping")}:</span>
              <span>
                {shippingPrice} {t("gel")}
              </span>
            </div>
            <div className="summary-line total">
              <span>{t("total")}:</span>
              <span>
                {totalPrice} {t("gel")}
              </span>
            </div>
          </div>
        </div>

        <div className="shipping-info-parent-container">
          <h4>{t("owner")}</h4>
          <div className="owner-form-grid">
            <div className="input-group">
              <input
                placeholder={t("full_name")}
                {...register("full_name")}
                className={errors.full_name ? "error" : ""}
              />
              {errors.full_name && (
                <p className="error-msg">{t("required_field")}</p>
              )}
            </div>

            <div className="input-group">
              <input
                type="number"
                placeholder={t("phone_number")}
                {...register("phone_number")}
                className={errors.phone_number ? "error" : ""}
              />
              {errors.phone_number && (
                <p className="error-msg">{t("required_field")}</p>
              )}
            </div>

            <div className="input-group">
              <input
                type="number"
                placeholder={t("second_phone_number")}
                {...register("second_phone_number")}
              />
            </div>

            <h4 style={{ margin: "20px 0 0 0" }}>{t("shipping")}</h4>

            <div className="input-group">
              <input
                placeholder={t("city")}
                {...register("city")}
                className={errors.city ? "error" : ""}
              />
              {errors.city && (
                <p className="error-msg">{t("required_field")}</p>
              )}
            </div>

            <div className="input-group">
              <input
                placeholder={t("address")}
                {...register("address")}
                className={errors.address ? "error" : ""}
              />
              {errors.address && (
                <p className="error-msg">{t("required_field")}</p>
              )}
            </div>
          </div>

          {isLoggedIn && (
            <label className="gift-checkbox">
              <input
                type="checkbox"
                checked={isGift}
                onChange={(e) => setIsGift(e.target.checked)}
              />
              <span>
                <strong>{t("gift_checkbox_title")}</strong>
                <small>{t("gift_checkbox_hint")}</small>
              </span>
            </label>
          )}

          <button type="submit" className="place-order-btn">
            {t("proceed to checkout")}
          </button>

          {/* <div className="shipping">
            <div className="shipping-header-flex">
              <FontAwesomeIcon icon={faTruckFast} />
              <p className="delivery">{t("delivery")}</p>
            </div>

            <div className="inside-container">
              <p className="delivery-prices">- {t("delivery description 1")}</p>
              <p className="delivery-prices">- {t("delivery description 2")}</p>
            </div>
          </div> */}

          <div className="shipping">
            <div className="shipping-header-flex">
              <FontAwesomeIcon icon={faTruckFast} />
              <p className="delivery">{t("delivery")}</p>
            </div>

            <div className="inside-container">
              <p className="delivery-prices">- {t("delivery description 1")}</p>
              {/* <p className="delivery-prices">- {t("delivery description 2")}</p> */}
              <p className="delivery-prices">- {t("delivery description 3")}</p>
              {/* <p className="delivery-report">{t("call us")} - 591 09 80 80</p> */}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
